import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Bot, User, Loader2, Trophy, HelpCircle, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contextIcons = {
  tournament: Trophy,
  support: HelpCircle,
  sales: ShoppingBag,
  general: Bot,
};

export function ChatPanel({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState('general');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, context]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/history?context=${context}`, { withCredentials: true });
      setMessages(data.messages || []);
    } catch (e) {
      console.error('Failed to load chat history:', e);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API}/chat/message`,
        { message: userMessage, context },
        { withCredentials: true }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, suggestions: data.suggestions }]);
    } catch (e) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        error: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  const ContextIcon = contextIcons[context];

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-full md:w-[400px] z-50 glass animate-slide-in">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png"
                alt="XO AI"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-white">XOClaw AI</h2>
              <p className="text-xs text-[#A1A1AA]">Powered by XO</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-chat-btn">
            <X className="w-5 h-5 text-[#A1A1AA]" />
          </Button>
        </div>

        {/* Context Tabs */}
        <div className="p-3 border-b border-white/10">
          <Tabs value={context} onValueChange={setContext}>
            <TabsList className="w-full bg-[#1A1A1E] grid grid-cols-4">
              <TabsTrigger value="general" className="text-xs" data-testid="chat-tab-general">
                <Bot className="w-3 h-3 mr-1" />
                General
              </TabsTrigger>
              <TabsTrigger value="tournament" className="text-xs" data-testid="chat-tab-tournament">
                <Trophy className="w-3 h-3 mr-1" />
                Tournaments
              </TabsTrigger>
              <TabsTrigger value="support" className="text-xs" data-testid="chat-tab-support">
                <HelpCircle className="w-3 h-3 mr-1" />
                Support
              </TabsTrigger>
              <TabsTrigger value="sales" className="text-xs" data-testid="chat-tab-sales">
                <ShoppingBag className="w-3 h-3 mr-1" />
                Premium
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <ContextIcon className="w-12 h-12 mx-auto text-[#00F0FF]/30 mb-4" />
                <p className="text-[#A1A1AA] text-sm">
                  {context === 'tournament' && "Ask me about tournaments, games, or how to register!"}
                  {context === 'support' && "Need help? I'm here for you!"}
                  {context === 'sales' && "Discover premium features and exclusive offers!"}
                  {context === 'general' && "Hi! I'm ArenaClaw. How can I help you today?"}
                </p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 animate-fade-in ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png"
                      alt="XO AI"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'} p-3`}>
                  <p className={`text-sm ${msg.error ? 'text-[#FF0055]' : 'text-white'}`}>{msg.content}</p>
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {msg.suggestions.map((sug, sidx) => (
                        <button
                          key={sidx}
                          onClick={() => handleSuggestionClick(sug)}
                          className="text-xs px-2 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors"
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-[#1A1A1E] flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3 animate-fade-in">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png"
                    alt="XO AI"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="chat-bubble-ai p-3">
                  <Loader2 className="w-4 h-4 animate-spin text-[#00F0FF]" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-white/10">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="flex gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-[#1A1A1E] border-[#27272A] text-white placeholder:text-[#A1A1AA] focus:ring-[#00F0FF]"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={loading || !input.trim()}
              className="bg-[#00F0FF] text-black hover:bg-[#33F3FF]"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
